<script lang="ts">
  /* What to do with positions the supplier no longer offers (spec
     specs/20260915_supplier_access_sync.md §2.3).

     The four answers the owner asked for, ticked together and applied at once:
     А hide from the public page, Б close for my clients now, В close for my
     clients once stock runs out (sell-off until then), Г archive. Nothing
     ticked is «оставить как есть»; «Решу позже» only closes the dialog, so the
     positions keep asking. Beside each answer is what it would change — on
     the site or not, how many clients it is open to, what is on the shelf. */
  import { formatDate } from "$lib/format";
  import { onMount } from "svelte";
  import { _ } from "svelte-i18n";
  import {
    decideSupplierGaps,
    fetchSupplierGaps,
    type SupplierGap,
  } from "$lib/supplierDirectory";

  let {
    slug,
    productIds,
    onclose,
  }: {
    slug: string;
    productIds: number[];
    /** `changed` — a decision was saved and the list should reload. */
    onclose: (changed: boolean) => void;
  } = $props();

  let gaps = $state<SupplierGap[]>([]);
  let loading = $state(true);
  let saving = $state(false);
  let error = $state("");

  let hidePublic = $state(false);
  let closeClients = $state(false);
  let closeWhenSoldOut = $state(false);
  let archive = $state(false);

  onMount(async () => {
    try {
      gaps = await fetchSupplierGaps(slug, productIds);
      // Preselect what fits: off the site if it is on it.
      hidePublic = gaps.some((g) => g.public_visible);
    } catch {
      error = "gap.dialog.error";
    } finally {
      loading = false;
    }
  });

  const onSite = $derived(gaps.filter((g) => g.public_visible).length);
  const openClients = $derived(gaps.reduce((n, g) => n + g.open_clients, 0));
  const toShops = $derived(gaps.some((g) => g.open_to_shops));
  const stock = $derived(gaps.reduce((n, g) => n + Number(g.stock || 0), 0));
  const nothingTicked = $derived(!hidePublic && !closeClients && !closeWhenSoldOut && !archive);

  function reasonText(g: SupplierGap): string {
    const values = {
      supplier: g.supplier_name || "-",
      date: g.since ? formatDate(g.since) : "-",
    };
    if (g.reason === "delisted") return $_("gap.reason.delisted", { values });
    if (g.reason === "link_ended") return $_("gap.reason.link_ended", { values });
    return $_("gap.reason.access_closed", { values });
  }

  async function apply() {
    saving = true;
    error = "";
    try {
      await decideSupplierGaps(slug, gaps.map((g) => g.product_id), {
        hide_public: hidePublic,
        close_clients: closeClients,
        close_when_sold_out: closeWhenSoldOut && !closeClients,
        archive,
      });
      onclose(true);
    } catch {
      error = "gap.dialog.error";
      saving = false;
    }
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape" && !saving) onclose(false);
  }
</script>

<svelte:window onkeydown={onKey} />

<div class="gd-backdrop" role="presentation" onclick={(e) => e.target === e.currentTarget && !saving && onclose(false)}>
  <div class="gd" role="dialog" aria-modal="true" aria-labelledby="gd-title">
    <header class="gd-head">
      <span class="material-icons-outlined gd-icon" aria-hidden="true">remove_shopping_cart</span>
      <h3 id="gd-title">
        {gaps.length > 1
          ? $_("gap.dialog.titleMany", { values: { count: gaps.length } })
          : $_("gap.dialog.title")}
      </h3>
      <button type="button" class="gd-x" onclick={() => onclose(false)} aria-label={$_("common.close")} disabled={saving}>
        <span class="material-icons-outlined">close</span>
      </button>
    </header>

    {#if loading}
      <p class="gd-msg">{$_("common.loading")}</p>
    {:else}
      <ul class="gd-items">
        {#each gaps.slice(0, 6) as g (g.product_id)}
          <li>
            <span class="gd-name">{g.name}</span>
            <span class="gd-reason">{reasonText(g)}</span>
          </li>
        {/each}
        {#if gaps.length > 6}
          <li class="gd-more">{$_("gap.dialog.more", { values: { count: gaps.length - 6 } })}</li>
        {/if}
      </ul>

      <p class="gd-lead">{$_("gap.dialog.lead")}</p>

      <div class="gd-options">
        <label class="gd-opt" class:gd-opt--muted={onSite === 0}>
          <input type="checkbox" bind:checked={hidePublic} disabled={saving || onSite === 0} />
          <span>
            <span class="gd-opt-title">{$_("gap.opt.hidePublic")}</span>
            <span class="gd-opt-hint">
              {onSite > 0 ? $_("gap.opt.hidePublic.on") : $_("gap.opt.hidePublic.off")}
            </span>
          </span>
        </label>

        <label class="gd-opt">
          <input
            type="checkbox"
            bind:checked={closeClients}
            disabled={saving}
            onchange={() => { if (closeClients) closeWhenSoldOut = false; }}
          />
          <span>
            <span class="gd-opt-title">{$_("gap.opt.closeClients")}</span>
            <span class="gd-opt-hint">
              {$_("gap.opt.closeClients.hint", { values: { count: openClients } })}{#if toShops}
                · {$_("gap.opt.closeClients.shops")}{/if}
            </span>
          </span>
        </label>

        <label class="gd-opt">
          <input
            type="checkbox"
            bind:checked={closeWhenSoldOut}
            disabled={saving}
            onchange={() => { if (closeWhenSoldOut) closeClients = false; }}
          />
          <span>
            <span class="gd-opt-title">{$_("gap.opt.soldOut")}</span>
            <span class="gd-opt-hint">
              {stock > 0
                ? $_("gap.opt.soldOut.hint", { values: { qty: stock } })
                : $_("gap.opt.soldOut.noStock")}
            </span>
          </span>
        </label>

        <label class="gd-opt">
          <input type="checkbox" bind:checked={archive} disabled={saving} />
          <span>
            <span class="gd-opt-title">{$_("gap.opt.archive")}</span>
            <span class="gd-opt-hint">{$_("gap.opt.archive.hint")}</span>
          </span>
        </label>
      </div>
    {/if}

    {#if error}<p class="gd-error">{$_(error)}</p>{/if}

    <footer class="gd-actions">
      <button type="button" class="btn btn--secondary" onclick={() => onclose(false)} disabled={saving}>
        {$_("gap.later")}
      </button>
      <button type="button" class="btn btn--primary" onclick={apply} disabled={saving || loading || gaps.length === 0}>
        {saving ? $_("common.saving") : nothingTicked ? $_("gap.keep") : $_("gap.apply")}
      </button>
    </footer>
  </div>
</div>

<style>
  .gd-backdrop {
    position: fixed; inset: 0; z-index: 1000; background: rgba(48, 56, 65, 0.45);
    display: flex; align-items: center; justify-content: center; padding: 16px;
  }
  .gd {
    background: #fff; border-radius: 10px; width: 100%; max-width: 520px; max-height: calc(100vh - 32px);
    overflow-y: auto; box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2); padding: 18px 20px;
    display: flex; flex-direction: column; gap: 12px;
  }
  .gd-head { display: flex; align-items: center; gap: 10px; }
  .gd-head h3 { margin: 0; flex: 1; font-size: 17px; color: var(--color-surface-darker); }
  .gd-icon { color: #c0392b; }
  .gd-x { border: none; background: none; cursor: pointer; color: var(--color-text-muted); padding: 4px; border-radius: 4px; display: flex; }
  .gd-x:hover { background: rgba(0, 0, 0, 0.05); }
  .gd-msg { margin: 0; color: var(--color-text-muted); font-size: 14px; }
  .gd-items { list-style: none; margin: 0; padding: 10px 12px; background: var(--color-bg); border-radius: 8px; display: flex; flex-direction: column; gap: 6px; }
  .gd-items li { display: flex; flex-direction: column; gap: 1px; }
  .gd-name { font-weight: 600; font-size: 13.5px; }
  .gd-reason { font-size: 12px; color: var(--color-text-muted); }
  .gd-more { font-size: 12px; color: var(--color-text-muted); }
  .gd-lead { margin: 0; font-size: 13.5px; color: var(--color-surface-darker); }
  .gd-options { display: flex; flex-direction: column; gap: 8px; }
  .gd-opt { display: flex; gap: 10px; align-items: flex-start; padding: 8px 10px; border: 1px solid var(--color-divider); border-radius: 8px; cursor: pointer; }
  .gd-opt input { margin-top: 3px; }
  .gd-opt > span { display: flex; flex-direction: column; gap: 2px; }
  .gd-opt--muted { opacity: 0.6; cursor: default; }
  .gd-opt-title { font-size: 14px; font-weight: 600; }
  .gd-opt-hint { font-size: 12px; color: var(--color-text-muted); }
  .gd-error { margin: 0; color: #c0392b; font-size: 13px; }
  .gd-actions { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
</style>
